import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { DomainService } from '../services/domain.service';
import { AuthService } from '../services/auth.service';
import { UserRoleType } from '../../shared/interfaces/auth.interfaces';

@Injectable({
  providedIn: 'root'
})
export class DomainAuthGuard implements CanActivate {

  constructor(
    private domainService: DomainService,
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    
    const config = this.domainService.getConfig();
    if (!config || !config.domain) {
      return false;
    }
    
    const requestedRoute = state.url;
    const currentDomain = this.domainService.getCurrentDomain();
    
    // Cek apakah ini domain auth (login/join)
    if (this.domainService.isAuthDomain()) {
      return this.handleAuthDomainAccess(currentDomain, requestedRoute);
    }
    
    // Untuk domain DSP/SSP, cek apakah user mencoba akses route auth
    if (this.isAuthRoute(requestedRoute)) {
      return this.handleAuthRouteRedirect(currentDomain, requestedRoute, config);
    }
    
    // Cek authentication untuk route lainnya di domain DSP/SSP
    if (!this.authService.isAuthenticated()) {
      return this.handleUnauthenticated(currentDomain, config);
    }
    
    // Cek role-based access
    return this.checkRoleAccess(requestedRoute, currentDomain);
  }

  private handleAuthDomainAccess(currentDomain: string, requestedRoute: string): boolean {
    // PROFILING FIX: Hapus auto-redirect untuk user yang sudah authenticated
    // Biarkan user mengakses login domain untuk proper authentication flow
    // Hanya redirect jika user mencoba akses route yang tidak diizinkan
    
    // Untuk domain join, tetap izinkan akses ke route confirmation, category, dan profiling
    if (currentDomain === 'join') {
      const accessLevel = this.authService.getAccessLevel();
      return this.domainService.isRouteAllowed(requestedRoute, accessLevel);
    }
    
    // Untuk domain login, izinkan akses tanpa auto-redirect
    // User bisa login ulang atau mengakses halaman login
    const accessLevel = this.authService.getAccessLevel();
    return this.domainService.isRouteAllowed(requestedRoute, accessLevel);
  }

  private isAuthRoute(url: string): boolean {
    return url.includes('/login') || 
           url.includes('/register') || 
           url.includes('/forgot-password') ||
           url.includes('/reset-password');
  }

  private handleAuthRouteRedirect(currentDomain: string, requestedRoute: string, config: any): boolean {
    // Redirect ke login domain jika mencoba akses route auth di DSP/SSP
    if (currentDomain === 'localhost') {
      const targetRoute = '/login';
      this.router.navigate([targetRoute]);
    } else {
      const loginUrl = config.loginDomain || this.domainService.getLoginUrl();
      window.location.href = loginUrl + (requestedRoute.includes('/login') ? '/login' : '');
    }
    return false;
  }

  private handleUnauthenticated(currentDomain: string, config: any): boolean {
    if (currentDomain === 'localhost') {
      this.router.navigate(['/login']);
    } else {
      const loginUrl = config.loginDomain || this.domainService.getLoginUrl();
      window.location.href = loginUrl;
    }
    return false;
  }

  private checkRoleAccess(requestedRoute: string, currentDomain: string): boolean {
    const userType = this.authService.getUserType();
    const accessLevel = this.authService.getAccessLevel();
    
    // Admin dan in-house dapat mengakses semua domain
    if (accessLevel === 'admin' || accessLevel === 'in-house') {
      return this.domainService.isRouteAllowed(requestedRoute, accessLevel);
    }
    
    // PERBAIKAN: Gunakan helper method untuk cek domain yang sesuai
    if (userType) {
      // Cast accessLevel and userType to expected types since we know they are compatible in this context
      return this.handleUserDomainCheck(
        userType, 
        requestedRoute, 
        accessLevel, 
        currentDomain
      );
    }
    
    // Cek apakah route diizinkan di domain ini
    if (!this.domainService.isRouteAllowed(requestedRoute, accessLevel)) {
      this.router.navigate(['/main']);
      return false;
    }
        
    return true;
  }

  private handleUserDomainCheck(
    userType: UserRoleType, 
    requestedRoute: string, 
    accessLevel: 'admin' | 'user' | 'in-house' | null, 
    currentDomain: string
  ): boolean {
    // Cek apakah user sudah berada di domain yang benar
    if (this.domainService.isUserInCorrectDomain(userType)) {
      // User sudah di domain yang benar, cek apakah route diizinkan
      return this.domainService.isRouteAllowed(requestedRoute, accessLevel);
    }
    
    return this.handleCrossDomainRedirect(userType, currentDomain);
  }

  private handleCrossDomainRedirect(userType: UserRoleType, currentDomain: string): boolean {
    // CROSS-DOMAIN FIX: Cek apakah sedang ada proses auth dari URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthParams = urlParams.has('token') && urlParams.has('user') && urlParams.has('refreshToken');
    
    // Jika ada auth parameters, biarkan AuthService memproses dulu tanpa redirect
    if (hasAuthParams) {
      return true; // Izinkan akses sementara, AuthService akan handle navigation
    }
    
    // CROSS-DOMAIN FIX: Tambahkan delay untuk memastikan cookies sudah tersedia
    // Cek apakah baru saja ada proses auth (cookies mungkin belum tersedia)
    const hasAuthCookies = this.authService.isAuthenticated();
    if (!hasAuthCookies) {
      this.waitForAuthCookies();
    }
    
    // User belum di domain yang benar, lakukan redirect
    const correctUrl = this.domainService.getRedirectUrl(userType);
    
    if (currentDomain === 'localhost') {
      this.router.navigate([correctUrl]);
    } else {
      this.redirectToCorrectDomain(correctUrl);
    }
    return false;
  }

  private waitForAuthCookies(): void {
    // Tunggu sebentar untuk cookies cross-domain
    setTimeout(() => {
      if (this.authService.isAuthenticated()) {
        // Auth cookies sudah tersedia, trigger navigation
        window.location.reload();
      }
    }, 100);
  }

  private redirectToCorrectDomain(correctUrl: string): void {
    // CROSS-DOMAIN FIX: Tambahkan pengecekan untuk mencegah redirect berulang
    const currentUrl = window.location.href.split('?')[0]; // Bandingkan tanpa parameter
    const targetUrl = correctUrl.split('?')[0];
    
    if (currentUrl !== targetUrl) {
      window.location.href = correctUrl;
    } else {
      console.log('⚠️ [DomainAuthGuard] Already at target URL, preventing redirect loop');
    }
  }
}